import { useState } from "react"
import { logger } from "@dw/services";

/**
 * An XHR with no timeout can hang indefinitely on a bad connection, and every caller gates its UI on
 * `isUploadingFile` - so a hung PUT left a composer whose send button span forever and whose cancel
 * was disabled, with the user's work trapped inside it. Two minutes is far longer than the 5 MB cap
 * needs on any connection worth waiting for.
 *
 * Callers with a much larger cap override it via `IFileUploadOptions.timeoutMs` - a 500MB class
 * video is still going at two minutes on any ordinary connection, and timing it out would look
 * exactly like a failure to the person waiting.
 */
const UPLOAD_TIMEOUT_MS = 120000;

export interface IFileUploadOptions {
  /**
   * Progress for THIS upload, 0..1.
   *
   * The hook also exposes `fileUploadProgress`, but that is one number per hook instance: a caller
   * sending several files can only say which file a percentage belongs to if it is handed the
   * percentage alongside the file.
   */
  onProgress?: (fraction: number) => void;
  /** Overrides {@link UPLOAD_TIMEOUT_MS} for a file whose size makes the default too tight. */
  timeoutMs?: number;
}

export const useFileUploader = () => {

  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  const uploadFile = (
    url: string,
    file: File,
    headers?: Record<string, string>,
    options?: IFileUploadOptions
  ): Promise<boolean> => {
    const timeoutMs = options?.timeoutMs ?? UPLOAD_TIMEOUT_MS;

    return new Promise((resolve) => {
      setIsUploadingFile(true);
      setFileUploadProgress(0);
      options?.onProgress?.(0);

      try {  
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url, true);

        xhr.setRequestHeader('Content-Type', file.type);
        if (headers) {
          Object.keys(headers).forEach(key => {
            xhr.setRequestHeader(key, headers[key]);
          })
        }
  
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentCompleted = event.loaded / event.total;
            // A caller that passed `onProgress` is tracking each file itself and does not read
            // `fileUploadProgress` — setting it anyway would re-render that caller on every tick of
            // every file for a number it never displays. A 500MB upload fires thousands of these.
            if (!options?.onProgress) {
              setFileUploadProgress(percentCompleted);
            }
            options?.onProgress?.(percentCompleted);
          }
        };
  
        xhr.onload = () => {
          setIsUploadingFile(false);
          setFileUploadProgress(1);
          options?.onProgress?.(1);

          const isSuccessStatus = xhr.status >= 200 && xhr.status < 300;
          if (!isSuccessStatus) {
            logger.error(`Failed to upload file to [${url}]`, {
              status: xhr.status,
              statusText: xhr.statusText,
              requestHeaders: headers,
              responseHeaders: xhr.getAllResponseHeaders()
            });
          }

          resolve(isSuccessStatus);
        };
  
        xhr.onerror = () => {
          setIsUploadingFile(false);
          logger.error(`Failed to upload file to [${url}]`, {
            status: xhr.status,
            statusText: xhr.statusText,
            requestHeaders: headers,
            responseHeaders: xhr.getAllResponseHeaders()
          });
          resolve(false);
        };

        // Both settle the promise the same way `onerror` does - a caller only ever has to handle
        // `false`, so this is behaviour it already copes with rather than a new outcome.
        xhr.ontimeout = () => {
          setIsUploadingFile(false);
          logger.error(`Timed out uploading file to [${url}]`, { timeoutMs });
          resolve(false);
        };

        // Deliberately silent. The usual cause of an abort is the page being navigated away from or
        // unloaded mid-upload, which is the user's own doing rather than a fault - and since only
        // `logger.error` reaches New Relic, logging it would file a recurring alert for ordinary
        // browsing. The promise still settles, which is the part callers depend on.
        xhr.onabort = () => {
          setIsUploadingFile(false);
          resolve(false);
        };

        xhr.timeout = timeoutMs;
        xhr.send(file);
      } catch (err) {
        setIsUploadingFile(false);
        logger.error(`Error during useFileUploader::uploadFile to [${url}]`, err);
        resolve(false);
      }
    });
  }

  return {
    uploadFile,
    fileUploadProgress,
    isUploadingFile
  }
}